import {
  initialApiKeys,
  initialAuditLog,
  initialTeamMembers,
  initialWebhooks
} from '../../mocks/settings';

/**
 * Settings slice — team, API keys, webhooks, and the audit trail (T10).
 * Every mutation appends an audit entry (T10.3). The audit log lives in the
 * store (not mocks) so the trail captures this session's mutations.
 */

let auditCounter = 0;

function auditEntry(actor, action, detail) {
  auditCounter += 1;
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return {
    id: `audit-new-${auditCounter}`,
    actor,
    action,
    detail,
    at: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`
  };
}

export const createSettingsSlice = (set, get) => ({
  teamMembers: initialTeamMembers,
  apiKeys: initialApiKeys,
  webhooks: initialWebhooks,
  auditLog: initialAuditLog,

  recordAudit: (action, detail) => {
    const actor = get().session?.user?.name || 'System';
    set((state) => ({
      auditLog: [auditEntry(actor, action, detail), ...state.auditLog]
    }));
  },

  inviteTeamMember: ({ email, role }) => {
    const actor = get().session?.user?.name || 'System';
    const member = {
      id: `invite-${Date.now()}`,
      name: email.split('@')[0],
      email,
      role,
      status: 'invited',
      lastActive: '—'
    };
    set((state) => ({
      teamMembers: [...state.teamMembers, member],
      auditLog: [auditEntry(actor, 'team.invited', `${email} as ${role}`), ...state.auditLog]
    }));
    return member;
  },

  setTeamMemberRole: (memberId, role) => {
    const actor = get().session?.user?.name || 'System';
    set((state) => {
      const member = state.teamMembers.find((entry) => entry.id === memberId);
      return {
        teamMembers: state.teamMembers.map((entry) => (
          entry.id === memberId ? { ...entry, role } : entry
        )),
        auditLog: [auditEntry(actor, 'team.role_changed', `${member?.email || memberId} → ${role}`), ...state.auditLog]
      };
    });
  },

  removeTeamMember: (memberId) => {
    const actor = get().session?.user?.name || 'System';
    set((state) => {
      const member = state.teamMembers.find((entry) => entry.id === memberId);
      return {
        teamMembers: state.teamMembers.filter((entry) => entry.id !== memberId),
        auditLog: [auditEntry(actor, 'team.removed', member?.email || memberId), ...state.auditLog]
      };
    });
  },

  createApiKey: ({ label, scopes }) => {
    const actor = get().session?.user?.name || 'System';
    const random = Math.random().toString(16).slice(2, 6);
    const key = {
      id: `key-${Date.now()}`,
      label,
      prefix: `agi_live_${random}`,
      createdAt: new Date().toISOString().slice(0, 10),
      lastUsed: '—',
      scopes
    };
    set((state) => ({
      apiKeys: [...state.apiKeys, key],
      auditLog: [auditEntry(actor, 'apikey.created', `${label} (${scopes.join(', ') || 'no scopes'})`), ...state.auditLog]
    }));
    return key;
  },

  revokeApiKey: (keyId) => {
    const actor = get().session?.user?.name || 'System';
    set((state) => {
      const key = state.apiKeys.find((entry) => entry.id === keyId);
      return {
        apiKeys: state.apiKeys.filter((entry) => entry.id !== keyId),
        auditLog: [auditEntry(actor, 'apikey.revoked', key?.label || keyId), ...state.auditLog]
      };
    });
  },

  createWebhook: ({ label, url, events }) => {
    const actor = get().session?.user?.name || 'System';
    const webhook = {
      id: `wh-${Date.now()}`,
      label,
      url,
      events,
      enabled: true,
      lastDelivery: '—'
    };
    set((state) => ({
      webhooks: [...state.webhooks, webhook],
      auditLog: [auditEntry(actor, 'webhook.created', `${label} → ${url}`), ...state.auditLog]
    }));
    return webhook;
  },

  toggleWebhook: (webhookId) => {
    const actor = get().session?.user?.name || 'System';
    set((state) => {
      const webhook = state.webhooks.find((entry) => entry.id === webhookId);
      const enabled = !(webhook?.enabled ?? false);
      return {
        webhooks: state.webhooks.map((entry) => (
          entry.id === webhookId ? { ...entry, enabled } : entry
        )),
        auditLog: [auditEntry(actor, enabled ? 'webhook.enabled' : 'webhook.disabled', webhook?.label || webhookId), ...state.auditLog]
      };
    });
  },

  deleteWebhook: (webhookId) => {
    const actor = get().session?.user?.name || 'System';
    set((state) => {
      const webhook = state.webhooks.find((entry) => entry.id === webhookId);
      return {
        webhooks: state.webhooks.filter((entry) => entry.id !== webhookId),
        auditLog: [auditEntry(actor, 'webhook.deleted', webhook?.label || webhookId), ...state.auditLog]
      };
    });
  }
});
